/**
 * Turns the slideshow inside it into a continuously running marquee.
 * The cards are cloned so the loop is seamless, and the motion pauses while the
 * pointer is over the carousel (or focus is inside it) so cards stay clickable.
 *
 * @example
 * <carousel-marquee speed="60">
 *   <slideshow-component>...</slideshow-component>
 * </carousel-marquee>
 */
class CarouselMarquee extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLElement[]} */
  #clones = [];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #frame = 0;
  #lastTime = 0;
  #position = 0;
  #loopWidth = 0;
  #direction = 1;
  #visible = true;
  #touching = false;
  #touchTimeout = 0;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // The slideshow renders its slides in the light DOM, so wait one frame for the markup to be ready.
    requestAnimationFrame(() => this.#setup());
  }

  disconnectedCallback() {
    this.#teardown();
  }

  /** Pixels per second. */
  get speed() {
    const value = parseFloat(this.getAttribute('speed') ?? '');

    return Number.isNaN(value) ? 60 : value;
  }

  #setup() {
    if (!this.isConnected || this.#scroller) return;

    const scroller = this.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    const cards = Array.from(scroller.children).filter((child) => child.matches('.card'));
    if (cards.length === 0) return;

    this.#scroller = scroller;
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.setAttribute('running', '');
    scroller.style.scrollSnapType = 'none';
    scroller.style.scrollBehavior = 'auto';
    scroller.style.overflowX = 'hidden';

    this.#cloneCards(scroller, cards);
    this.#measure();
    this.#direction = getComputedStyle(scroller).direction === 'rtl' ? -1 : 1;

    this.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    this.addEventListener('touchstart', this.#handleTouchStart, { signal, passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd, { signal, passive: true });
    this.addEventListener('touchcancel', this.#handleTouchEnd, { signal, passive: true });
    window.addEventListener('resize', this.#measure, { signal });

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
    });
    this.#observer.observe(this);

    this.#frame = requestAnimationFrame(this.#tick);
  }

  #teardown() {
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#touchTimeout);
    this.#abortController?.abort();
    this.#observer?.disconnect();

    for (const clone of this.#clones) clone.remove();
    this.#clones = [];

    if (this.#scroller) {
      this.#scroller.style.removeProperty('scroll-snap-type');
      this.#scroller.style.removeProperty('scroll-behavior');
      this.#scroller.style.removeProperty('overflow-x');
      this.#scroller.scrollLeft = 0;
    }

    this.removeAttribute('running');
    this.#scroller = null;
    this.#position = 0;
    this.#lastTime = 0;
    this.#touching = false;
  }

  /**
   * Appends copies of the cards until there is always a full viewport of cards after the loop point.
   * @param {HTMLElement} scroller
   * @param {Element[]} cards
   */
  #cloneCards(scroller, cards) {
    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const setWidth = cards.reduce((total, card) => total + card.getBoundingClientRect().width + gap, 0);
    if (setWidth === 0) return;

    const copies = Math.max(1, Math.ceil(scroller.clientWidth / setWidth));

    for (let copy = 0; copy < copies; copy++) {
      for (const card of cards) {
        const clone = /** @type {HTMLElement} */ (card.cloneNode(true));

        clone.removeAttribute('ref');
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });

        scroller.append(clone);
        this.#clones.push(clone);
      }
    }
  }

  #measure = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const [firstCard] = Array.from(scroller.children).filter((child) => child.matches('.card'));
    const [firstClone] = this.#clones;
    if (!firstCard || !firstClone) return;

    // Distance between a card and its first copy is exactly one full set of cards.
    this.#loopWidth = /** @type {HTMLElement} */ (firstClone).offsetLeft - /** @type {HTMLElement} */ (firstCard).offsetLeft;
  };

  get #paused() {
    return this.#touching || !this.#visible || document.hidden || this.matches(':hover') || this.matches(':focus-within');
  }

  /** @param {number} time */
  #tick = (time) => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const elapsed = this.#lastTime ? Math.min(time - this.#lastTime, 100) : 0;
    this.#lastTime = time;

    if (!this.#paused && this.#loopWidth > 0) {
      this.#position += (this.speed * elapsed) / 1000;
      if (this.#position >= this.#loopWidth) this.#position -= this.#loopWidth;

      scroller.scrollLeft = this.#position * this.#direction;
    }

    this.#frame = requestAnimationFrame(this.#tick);
  };

  #handlePointerEnter = () => {
    // Keep the position in sync if the browser moved it (e.g. the user dragged or tabbed to a card).
    if (this.#scroller) this.#position = Math.abs(this.#scroller.scrollLeft);
  };

  #handleTouchStart = () => {
    clearTimeout(this.#touchTimeout);
    this.#touching = true;
  };

  #handleTouchEnd = () => {
    clearTimeout(this.#touchTimeout);
    this.#touchTimeout = window.setTimeout(() => {
      this.#touching = false;
    }, 1500);
  };
}

if (!customElements.get('carousel-marquee')) {
  customElements.define('carousel-marquee', CarouselMarquee);
}
